import { useEffect, useRef, useState } from 'react'

import { maxExistingInvoiceNumber, updateSettings, watchSettings } from '../lib/data.js'
import { verifyResendConnection } from '../lib/stripe.js'

export default function Settings() {
  const [settings, setSettings] = useState({})
  const [draft, setDraft] = useState({})
  const [savedAt, setSavedAt] = useState(0)
  const [emailBusy, setEmailBusy] = useState(false)
  const [emailMsg, setEmailMsg] = useState('')

  // Seed the draft from settings ONLY on the first load. Subsequent RTDB
  // writes (e.g. the Test Now button writing settings/emailHealth) must NOT
  // overwrite the draft, or any in-progress edit (like the freshly pasted
  // Resend API key) would get cleared.
  const draftInitialized = useRef(false)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => watchSettings((s) => {
    const next = s || {}
    setSettings(next)
    setLoaded(true)
    if (!draftInitialized.current) {
      setDraft(next)
      draftInitialized.current = true
    }
  }), [])

  const handleSaveKeyAndTest = async () => {
    setEmailBusy(true)
    setEmailMsg('Saving and testing Resend API key…')
    try {
      const key = (draft.resendApiKey || '').trim()
      if (!key) {
        setEmailMsg('Paste a Resend API key first, then click Save & Test.')
        return
      }
      // Save the key to RTDB BEFORE asking the server to test it.
      // Otherwise the server reads the previously-saved (empty) value.
      await updateSettings({ resendApiKey: key })
      setSavedAt(Date.now())
      const r = await verifyResendConnection()
      if (r.status === 'ok') setEmailMsg('✓ Resend API key saved and validated — emails will send.')
      else if (r.status === 'not_configured') setEmailMsg('Key saved but server reports not_configured — refresh and retry.')
      else setEmailMsg(`✗ Saved, but Resend rejected the key: ${r.error || 'unknown error'}`)
    } catch (e) {
      setEmailMsg(`Save/test failed: ${e.message}`)
    } finally {
      setEmailBusy(false)
    }
  }

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }))

  // Live floor for the invoice-number field: it may never be set at or below
  // the highest existing invoice number, or the next invoice would duplicate.
  const [invoiceMax, setInvoiceMax] = useState(null)
  const [numberWarn, setNumberWarn] = useState('')
  useEffect(() => { maxExistingInvoiceNumber().then(setInvoiceMax).catch(() => {}) }, [savedAt])

  const save = async (e) => {
    e?.preventDefault()
    setNumberWarn('')
    // LOCK: refuse any nextInvoiceNumber at/below the highest existing invoice.
    // Clamp to max+1 so a manual edit can never cause a duplicate.
    const max = await maxExistingInvoiceNumber()
    const entered = Number(draft.nextInvoiceNumber) || Number(settings.nextInvoiceNumber) || 1001
    let nextNum = entered
    if (entered <= max) {
      nextNum = max + 1
      setDraft((d) => ({ ...d, nextInvoiceNumber: nextNum }))
      setNumberWarn(`"${entered}" is at or below the highest existing invoice (#${max}) and would create a duplicate — clamped to ${nextNum}.`)
    }
    await updateSettings({
      companyName: draft.companyName || '',
      companyAddress: draft.companyAddress || '',
      companyPhone: draft.companyPhone || '',
      companyEmail: draft.companyEmail || '',
      emailSubject: draft.emailSubject || '',
      emailIntro: draft.emailIntro || '',
      resendApiKey: (draft.resendApiKey || '').trim(),
      nextInvoiceNumber: nextNum,
    })
    setSavedAt(Date.now())
  }

  const health = settings.emailHealth || null
  const hasKey = !!(settings.resendApiKey || '').trim()
  const healthy = hasKey && health && health.status === 'ok'
  const broken  = hasKey && health && health.status === 'error'
  // Distinguish "still loading from RTDB" from "loaded, no key". Avoids
  // the brief "No API key saved" flicker on first render and the
  // permanent-stuck state if the watcher never fires (e.g. unauthenticated).
  const dotColor = !loaded ? 'bg-gray-300'
                 : healthy ? 'bg-green-600'
                 : broken  ? 'bg-red-600'
                 : hasKey  ? 'bg-amber-500'
                 : 'bg-gray-400'
  const label   = !loaded ? 'Loading…'
                 : healthy ? 'Connected'
                 : broken  ? 'Key rejected'
                 : hasKey  ? 'Saved (untested)'
                 : 'No API key saved'

  return (
    <form onSubmit={save} className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-extrabold">Settings</h1>

      <div className="card space-y-3">
        <h2 className="font-extrabold">Company</h2>
        <Field label="Company name">
          <input className="input" value={draft.companyName || ''} onChange={(e) => set('companyName', e.target.value)} />
        </Field>
        <Field label="Address">
          <input className="input" value={draft.companyAddress || ''} onChange={(e) => set('companyAddress', e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Phone">
            <input className="input" value={draft.companyPhone || ''} onChange={(e) => set('companyPhone', e.target.value)} />
          </Field>
          <Field label="Email (reply-to)">
            <input type="email" className="input" value={draft.companyEmail || ''} onChange={(e) => set('companyEmail', e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="card space-y-3">
        <h2 className="font-extrabold">Email delivery (Resend)</h2>
        <p className="text-sm text-gray-700">
          All app emails (invoices, signup, receipts, reminders) send through
          Resend. Paste your API key from <a className="text-brand-blue underline" target="_blank" rel="noreferrer" href="https://resend.com/api-keys">resend.com/api-keys</a>.
          Replies route to <b>yesnetwifi@gmail.com</b>.
        </p>
        <Field label="Resend API key">
          <input
            type="password"
            className="input font-mono"
            placeholder={hasKey ? '•••••••••••• (saved — paste a new key to replace)' : 're_…'}
            autoComplete="off"
            value={draft.resendApiKey || ''}
            onChange={(e) => set('resendApiKey', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Paste your key, then click <b>Save &amp; Test</b>. The key is
            stored at <code>settings/resendApiKey</code> in Firebase RTDB.
          </p>
        </Field>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm">
            <span className={`inline-block w-2 h-2 rounded-full ${dotColor} mr-2 align-middle`} />
            <b>{label}</b>
            {health?.checkedAt ? (
              <div className="text-xs text-gray-500 mt-1">
                Last checked {new Date(health.checkedAt).toLocaleString()}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSaveKeyAndTest}
            disabled={emailBusy}>
            {emailBusy ? 'Working…' : 'Save & Test'}
          </button>
        </div>
        {broken ? (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm">
            <div className="font-bold text-red-700 mb-1">
              ⚠️ Resend rejected the API key — emails are not sending
            </div>
            {health.error ? (
              <div className="text-xs text-red-700/80 font-mono break-all mb-2">
                {health.error}
              </div>
            ) : null}
            <div className="text-xs text-red-700">
              Generate a new key at resend.com/api-keys, paste it above, and click Save.
            </div>
          </div>
        ) : null}
        {emailMsg ? (
          <div className="text-xs text-gray-600">{emailMsg}</div>
        ) : null}
      </div>

      <div className="card space-y-3">
        <h2 className="font-extrabold">Invoice email template</h2>
        <Field label="Subject">
          <input className="input" placeholder="Invoice #{n} from {company}" value={draft.emailSubject || ''} onChange={(e) => set('emailSubject', e.target.value)} />
        </Field>
        <Field label="Intro">
          <textarea className="input min-h-[80px]" placeholder="Hi {name}, thanks for being a customer…" value={draft.emailIntro || ''} onChange={(e) => set('emailIntro', e.target.value)} />
        </Field>
        <p className="text-xs text-gray-500">
          Available tokens: <code>{'{name}'}</code>, <code>{'{n}'}</code> (invoice #), <code>{'{amount}'}</code>, <code>{'{period}'}</code>, <code>{'{due}'}</code>, <code>{'{company}'}</code>, <code>{'{phone}'}</code>.
        </p>
      </div>

      <div className="card">
        <h2 className="font-extrabold">Invoice numbering</h2>
        <Field label="Next invoice number">
          <input type="number" className="input w-40" min={invoiceMax != null ? invoiceMax + 1 : undefined} value={draft.nextInvoiceNumber || ''} onChange={(e) => set('nextInvoiceNumber', e.target.value)} />
        </Field>
        <p className="text-xs text-gray-500 mt-1">
          Set once; auto-increments on every invoice generated.
          {invoiceMax != null ? ` Must be greater than the highest existing invoice (#${invoiceMax}); lower values are clamped to ${invoiceMax + 1} on save.` : ''}
        </p>
        {numberWarn ? <p className="text-xs text-red-600 mt-1 font-bold">{numberWarn}</p> : null}
      </div>

      <div className="flex justify-end gap-3 items-center">
        {savedAt ? <span className="text-xs text-brand-green-deep">Saved · {new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span> : null}
        <button className="btn-primary">Save settings</button>
      </div>
    </form>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  )
}

